import { useEffect, useRef, useState } from 'react';
import { compareVersions } from '../utils/semver';

export interface AgentStatus {
  status: 'checking' | 'online' | 'offline' | 'error';
  version: string | null;
  agentId: string | null;
  tenantId: string | null;
  lastChecked: Date | null;
  isOutdated: boolean;
}

interface VersionResponse {
  name?: string;
  version?: string;
  description?: string;
  platform?: string;
  arch?: string;
  nodeVersion?: string;
  pid?: number;
  uptimeSeconds?: number;
  packaged?: boolean;
  appRoot?: string;
  windowsStartup?: unknown;
  agentId?: string;
  tenantId?: string;
}

interface RequiredVersionResponse {
  requiredVersion: string;
}

async function fetchRequiredVersion(): Promise<string> {
  // TODO: Replace mock with real backend call to /agent/required-version
  try {
    const res = await fetch('/agent/required-version', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!res.ok) {
      // Fallback to hard-coded default if backend not ready
      return '1.0.0';
    }

    const data = (await res.json()) as RequiredVersionResponse;
    return data.requiredVersion || '1.0.0';
  } catch {
    // Silent failure: treat as baseline version
    return '1.0.0';
  }
}

async function tryLocalAgentVersion(url: string): Promise<VersionResponse | null> {
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!res.ok) {
      return null;
    }

    const data = (await res.json()) as VersionResponse;
    return data;
  } catch {
    // Network/CORS/connection errors are treated as offline, without logging noise.
    return null;
  }
}

export interface UseLocalAgentStatusOptions {
  intervalMs?: number;
  enabled?: boolean;
  maxBackoffMs?: number;
}

export function useLocalAgentStatus(
  options: UseLocalAgentStatusOptions = {}
): AgentStatus {
  const { intervalMs = 5000, enabled = false, maxBackoffMs = 120000 } = options;

  const [state, setState] = useState<AgentStatus>({
    status: 'checking',
    version: null,
    agentId: null,
    tenantId: null,
    lastChecked: null,
    isOutdated: false
  });

  const timerRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const failureCountRef = useRef(0);
  

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!enabled) {
      // When disabled, stop polling and reset to a quiet offline state.
      if (!cancelled && isMountedRef.current) {
        setState((prev) => ({
          ...prev,
          status: 'offline',
          version: null,
          agentId: null,
          tenantId: null,
          lastChecked: null,
          isOutdated: false,
        }));
      }
      return () => {
        cancelled = true;
      };
    }

    const poll = async () => {
      if (cancelled || !isMountedRef.current) return;

      try {
        const [requiredVersion, localInfo] = await Promise.all([
          fetchRequiredVersion(),
          fetchLocalAgentVersion()
        ]);

        const now = new Date();

        if (!localInfo || !localInfo.version) {
          failureCountRef.current = Math.min(failureCountRef.current + 1, 10);
          if (!cancelled && isMountedRef.current) {
            setState((prev) => ({
              ...prev,
              status: 'offline',
              version: null,
              agentId: null,
              tenantId: null,
              lastChecked: now,
              isOutdated: false
            }));
          }
        } else {
          failureCountRef.current = 0;
          const currentVersion = localInfo.version;
          const cmp = compareVersions(currentVersion, requiredVersion);
          const isOutdated = cmp < 0;

          if (!cancelled && isMountedRef.current) {
            setState({
              status: 'online',
              version: currentVersion,
              agentId: localInfo.agentId || null,
              tenantId: localInfo.tenantId || null,
              lastChecked: now,
              isOutdated
            });
          }
        }
      } catch {
        const now = new Date();
        failureCountRef.current = Math.min(failureCountRef.current + 1, 10);
        if (!cancelled && isMountedRef.current) {
          setState((prev) => ({
            ...prev,
            status: 'offline',
            lastChecked: now,
            version: null,
            agentId: null,
            tenantId: null,
            isOutdated: false
          }));
        }
      } finally {
        if (!cancelled && isMountedRef.current) {
          const failures = failureCountRef.current;
          const backoff = failures > 0
            ? Math.min(maxBackoffMs, intervalMs * Math.pow(2, Math.min(failures, 5)))
            : intervalMs;
          timerRef.current = window.setTimeout(poll, backoff);
        }
      }
    };

    // initial poll
    poll();

    return () => {
      cancelled = true;
    };
  }, [enabled, intervalMs, maxBackoffMs]);

  return state;
}

async function fetchLocalAgentVersion(): Promise<VersionResponse | null> {
  // NOTE: Implemented as a single-port probe per poll to reduce console noise.
  // Prefer a previously-good URL first.
  const urls = ['http://localhost:3000/api/version', 'http://localhost:30001/api/version'];

  // Attempt the preferred URL first if present.
  const preferred = (typeof window !== 'undefined' && (window as any).__LOCAL_AGENT_PREFERRED_URL__)
    ? String((window as any).__LOCAL_AGENT_PREFERRED_URL__)
    : null;
  if (preferred && urls.includes(preferred)) {
    const res = await tryLocalAgentVersion(preferred);
    if (res && res.agentId) return res;
  }

  // Otherwise, probe one URL per poll (alternating).
  const idx = (typeof window !== 'undefined' && typeof (window as any).__LOCAL_AGENT_PORT_IDX__ === 'number')
    ? (window as any).__LOCAL_AGENT_PORT_IDX__
    : 0;
  const url = urls[idx % urls.length];
  try {
    (window as any).__LOCAL_AGENT_PORT_IDX__ = (idx + 1) % urls.length;
  } catch (_) {}

  const data = await tryLocalAgentVersion(url);
  if (data && data.agentId) {
    try {
      (window as any).__LOCAL_AGENT_PREFERRED_URL__ = url;
    } catch (_) {}
    return data;
  }

  return null;
}
